import type { ArtifactExportPayload } from './artifact-export-route.type';

/**
 * Decodes an artifact export payload from the URL hash.
 *
 * - Strips a leading '#' from the hash.
 * - Extracts the 'payload' parameter using a custom regex.
 * - Normalizes and decodes base64 (supports URL-safe variants).
 * - Converts binary to UTF-8 string, then parses JSON.
 * - Returns the payload if valid, else null.
 *
 * @param {string} hash - URL hash fragment (with or without '#')
 * @returns {ArtifactExportPayload | null} The decoded and validated export payload, or null.
 */
function decodeArtifactPayload(hash: string): ArtifactExportPayload | null {
  if (!hash) return null;
  const fragment = hash.startsWith('#') ? hash.slice(1) : hash;
  if (!fragment) return null;

  // Parse `payload=...` manually — do NOT use URLSearchParams here because it
  // URL-decodes the value, which silently converts standard-base64 `+` into a
  // space and corrupts the payload before `atob` sees it. The regex enforces
  // a key boundary (start-of-string or `&`) so substrings inside other
  // parameter names (e.g. `xpayload=...`) cannot be mistakenly extracted, and
  // tolerates both standard and URL-safe base64 alphabets.
  const match = fragment.match(/(?:^|&)payload=([^&]*)/);
  if (!match) return null;
  const raw = match[1] ?? '';
  if (!raw) return null;

  // Normalize URL-safe base64 → standard base64. atob requires `+/` and padding.
  const normalized = raw.replace(/-/g, '+').replace(/_/g, '/');
  const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=');

  let json: string;
  try {
    // atob returns a binary string (one byte per code unit). UTF-8 multi-byte
    // sequences must be reconstructed into a Uint8Array and decoded with
    // TextDecoder, otherwise non-ASCII characters in the payload (e.g.
    // accented work-period names) get silently mojibake'd before JSON.parse.
    const binary = atob(padded);
    const bytes = Uint8Array.from(binary, (ch) => ch.charCodeAt(0));
    json = new TextDecoder('utf-8', { fatal: true }).decode(bytes);
  } catch {
    return null;
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch {
    return null;
  }

  if (!isPayloadShape(parsed)) return null;
  return parsed;
}

/**
 * Type guard for the minimal `{ data, config?, options? }` shape we accept
 * from the BE. `data` must be a plain object; `config` and `options` are
 * optional plain objects.
 *
 * @param {unknown} value - Candidate value to validate.
 * @returns {boolean} True when `value` satisfies the export payload shape.
 */
function isPayloadShape(value: unknown): value is ArtifactExportPayload {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const candidate = value as Record<string, unknown>;
  if (!candidate.data || typeof candidate.data !== 'object' || Array.isArray(candidate.data)) {
    return false;
  }
  if (
    candidate.config !== undefined &&
    (candidate.config === null || typeof candidate.config !== 'object' || Array.isArray(candidate.config))
  ) {
    return false;
  }
  if (
    candidate.options !== undefined &&
    (candidate.options === null || typeof candidate.options !== 'object' || Array.isArray(candidate.options))
  ) {
    return false;
  }
  if (candidate.renders !== undefined) {
    if (!Array.isArray(candidate.renders)) return false;
    for (const entry of candidate.renders) {
      if (!entry || typeof entry !== 'object' || Array.isArray(entry)) return false;
      const entryRecord = entry as Record<string, unknown>;
      if (
        entryRecord.options !== undefined &&
        (entryRecord.options === null || typeof entryRecord.options !== 'object' || Array.isArray(entryRecord.options))
      ) {
        return false;
      }
      if (entryRecord.pageTitle !== undefined && typeof entryRecord.pageTitle !== 'string') {
        return false;
      }
    }
  }
  return true;
}

export { decodeArtifactPayload };
